import api from "./api";

export interface StudentProgressData {
  studentId: string;
  studentName: string;
  courseId: string;
  courseName: string;
  overallProgress: number;
  modulesCompleted: number;
  totalModules: number;
  timeSpent: number;
  lastAccessedAt: string;
  startedAt: string;
}

export interface CourseCompletionData {
  courseId: string;
  courseName: string;
  totalEnrollments: number;
  completedEnrollments: number;
  averageCompletion: number;
  completionRate: number;
}

export interface QuizPerformanceData {
  quizId: string;
  quizTitle: string;
  totalAttempts: number;
  averageScore: number;
  highestScore: number;
  lowestScore: number;
  passCount: number;
  passRate: number;
  averageTimeSpent: number;
  attempts: Array<{
    studentName: string;
    score: number;
    passed: boolean;
    submittedAt: string;
    timeSpent: number;
  }>;
}

export interface AnalyticsDashboardData {
  overview: {
    totalStudents: number;
    totalCourses: number;
    totalEnrollments: number;
    totalQuizzes: number;
    completionRate: number;
    quizPassRate: number;
  };
}

/**
 * Get analytics dashboard overview
 */
export const getAnalyticsDashboard = async (): Promise<{
  success: boolean;
  data: AnalyticsDashboardData;
}> => {
  try {
    const response = await api.get("/elearning/analytics/dashboard");
    return {
      success: (response as any).success,
      data: (response as any).data,
    };
  } catch (error) {
    console.error("Error fetching analytics dashboard:", error);
    return {
      success: false,
      data: {
        overview: {
          totalStudents: 0,
          totalCourses: 0,
          totalEnrollments: 0,
          totalQuizzes: 0,
          completionRate: 0,
          quizPassRate: 0,
        },
      },
    };
  }
};

/**
 * Get student progress analytics
 */
export const getStudentProgressAnalytics = async (params?: {
  courseId?: string;
  studentId?: string;
  branchId?: string;
}): Promise<{ success: boolean; data: StudentProgressData[] }> => {
  try {
    const queryParams = new URLSearchParams();
    if (params?.courseId) queryParams.append("courseId", params.courseId);
    if (params?.studentId) queryParams.append("studentId", params.studentId);
    if (params?.branchId) queryParams.append("branchId", params.branchId);

    const response = await api.get(
      `/elearning/analytics/student-progress?${queryParams.toString()}`
    );
    return {
      success: (response as any).success,
      data: (response as any).data || [],
    };
  } catch (error) {
    console.error("Error fetching student progress analytics:", error);
    return {
      success: false,
      data: [],
    };
  }
};

/**
 * Get course completion analytics
 */
export const getCourseCompletionAnalytics = async (params?: {
  courseId?: string;
  branchId?: string;
  startDate?: string;
  endDate?: string;
}): Promise<{ success: boolean; data: CourseCompletionData[] }> => {
  try {
    const queryParams = new URLSearchParams();
    if (params?.courseId) queryParams.append("courseId", params.courseId);
    if (params?.branchId) queryParams.append("branchId", params.branchId);
    if (params?.startDate) queryParams.append("startDate", params.startDate);
    if (params?.endDate) queryParams.append("endDate", params.endDate);

    const response = await api.get(
      `/elearning/analytics/course-completion?${queryParams}`
    );
    return {
      success: (response as any).success,
      data: (response as any).data || [],
    };
  } catch (error) {
    console.error("Error fetching course completion analytics:", error);
    return {
      success: false,
      data: [],
    };
  }
};

/**
 * Get quiz performance analytics
 */
export const getQuizPerformanceAnalytics = async (params?: {
  quizId?: string;
  courseId?: string;
  branchId?: string;
  startDate?: string;
  endDate?: string;
}): Promise<{ success: boolean; data: QuizPerformanceData[] }> => {
  try {
    const queryParams = new URLSearchParams();
    if (params?.quizId) queryParams.append("quizId", params.quizId);
    if (params?.courseId) queryParams.append("courseId", params.courseId);
    if (params?.branchId) queryParams.append("branchId", params.branchId);
    if (params?.startDate) queryParams.append("startDate", params.startDate);
    if (params?.endDate) queryParams.append("endDate", params.endDate);

    const response = await api.get(
      `/elearning/analytics/quiz-performance?${queryParams}`
    );
    return {
      success: (response as any).success,
      data: (response as any).data || [],
    };
  } catch (error) {
    console.error("Error fetching quiz performance analytics:", error);
    return {
      success: false,
      data: [],
    };
  }
};
